// React 19 removed `findDOMNode`. Direct callers in this app are gone (Semantic UI
// removed; react-draggable usages all pass `nodeRef`; react-grid-layout 1.5+ passes
// nodeRef internally; react-sizeme is aliased to a stub), so no polyfill is needed.
// Keep this defensive global shim as a safety net for any third-party script that
// reads `window.ReactDOM.findDOMNode` at runtime.
const findDOMNodeShim = instance => {
  if (instance == null) return null;
  if (instance instanceof Element) return instance;
  return instance && instance.nodeType ? instance : null;
};
if (typeof window !== 'undefined') {
  window.ReactDOM = window.ReactDOM ?? {};
  if (!window.ReactDOM.findDOMNode) window.ReactDOM.findDOMNode = findDOMNodeShim;
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
  provideGlobalGridOptions,
} from 'ag-grid-enterprise';
import { registerLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './i18n';
import KeycloakProvider from './components/Auth/KeycloakProvider';
import ThemeModeProvider from './styles/ThemeModeProvider';
// Side-effect import: registers beforeinstallprompt listener at module load,
// before KeycloakProvider's SSO roundtrip can drop the one-shot event.
import './components/PwaInstallBanner/installPromptCapture';
import { registerAppServiceWorker } from './registerServiceWorker';
import { ACTIVE_ROLES_CHANGED_EVENT } from './config/roles';
import { applyActiveRolesToUserCache } from './behavior/init/hooks/useUserQuery';
import './index.less';
import App from './App';
import { MediaContextProvider, mediaStyles } from './mediaStyles';
import queryClient from './queryClient';
import { LayoutProvider } from './context/LayoutContext';

registerLocale('en-GB', enGB);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-076979}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{CERN}_is_granted_a_{Multiple_Applications}_Developer_License_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{14_March_2026}____[v3]_[01]_MTc3MzQ0NjQwMDAwMA==988e0de08bb67eea2063f990e1d4f53d'
);

ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: 'legacy' });

if (typeof window !== 'undefined') {
  window.addEventListener(ACTIVE_ROLES_CHANGED_EVENT, event => {
    const roles = Array.isArray(event.detail) ? event.detail : [];
    Promise.resolve().then(() => {
      applyActiveRolesToUserCache(queryClient, roles);
    });
  });
}

const enhanceAgGridDialogs = () => {
  document
    .querySelectorAll('.ag-panel-title-bar-button:not([data-remus-enhanced])')
    .forEach(button => {
      button.setAttribute('data-remus-enhanced', 'true');
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', 'Close');
      button.setAttribute('tabindex', '0');
      button.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          button.click();
        }
      });
    });
};

if (typeof window !== 'undefined') {
  const agGridDialogObserver = new MutationObserver(enhanceAgGridDialogs);
  agGridDialogObserver.observe(document.body, { childList: true, subtree: true });
  enhanceAgGridDialogs();
}

registerAppServiceWorker();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <>
    <style>{mediaStyles}</style>
    <ThemeModeProvider>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <MediaContextProvider>
          <KeycloakProvider>
            <LayoutProvider>
              <App />
            </LayoutProvider>
          </KeycloakProvider>
        </MediaContextProvider>
      </QueryClientProvider>
    </ThemeModeProvider>
  </>
);
